/**
 * AIChatPanel - Lightweight AI chat panel
 * No Redux, no dashboard AI services — standalone
 */

import { useState, useEffect, useRef } from 'react'
import { Sparkles, X, Send, Loader2, Maximize2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { BACKEND_API_URL } from './types'

export function AIChatPanel({
  isOpen,
  onClose,
  aiHubUrl = 'https://dashboard.quotech.ai/ai-hub',
}: {
  isOpen: boolean
  onClose: () => void
  aiHubUrl?: string
}) {
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([])
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (isOpen && textareaRef.current) setTimeout(() => textareaRef.current?.focus(), 100)
  }, [isOpen])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 80) + 'px'
    }
  }, [input])

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!input.trim() || isLoading) return

    const userMessage = input.trim()
    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }])
    setIsLoading(true)

    try {
      const token = localStorage.getItem('IdToken') || localStorage.getItem('access_token')
      const orgId = localStorage.getItem('org_id') || localStorage.getItem('CurrentTenantId')

      const res = await fetch(`${BACKEND_API_URL}/api/ai/chat/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId ? { 'X-Organisation-ID': orgId } : {}),
        },
        body: JSON.stringify({
          message: userMessage,
          context: { page: window.location.pathname },
        }),
      })

      if (!res.ok) throw new Error('AI request failed')
      const data = await res.json()
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response || data.message || 'No response' }])
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Try the full AI Hub for better results.' }])
    } finally {
      setIsLoading(false)
    }
  }

  const quickPrompts = [
    { label: "Today's Focus", prompt: 'What should I focus on today?' },
    { label: 'At Risk Jobs', prompt: 'Which jobs need attention?' },
    { label: 'Cash Position', prompt: "What's our cash position?" },
  ]

  if (!isOpen) return null

  return (
    <div className="w-[400px] max-w-[calc(100vw-2rem)] bg-slate-900 rounded-t-2xl shadow-2xl border border-slate-700/50 border-b-0 flex flex-col animate-in slide-in-from-bottom-4 duration-300">
      {/* Header — mirrors the AI Hub header (slate surface + teal→cyan accent) */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800/50 border-b border-slate-700/50 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="text-sm font-semibold text-slate-100">QuotechAI</span>
            <p className="text-[10px] text-slate-400">{messages.length} messages</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <a
            href={aiHubUrl}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 rounded-lg transition-colors"
            title="Open full AI Hub"
          >
            <Maximize2 className="w-4 h-4" />
          </a>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[260px] max-h-[380px] bg-slate-900">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-teal-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-200 mb-1">QuotechAI Assistant</h3>
            <p className="text-xs text-slate-500 mb-4">Ask about jobs, margins, cash flow, or anything</p>
            <div className="flex flex-wrap justify-center gap-2">
              {quickPrompts.map((qp, i) => (
                <button
                  key={i}
                  onClick={() => setInput(qp.prompt)}
                  className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-700/50 hover:text-teal-300 transition-colors"
                >
                  {qp.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={cn('flex gap-2.5', msg.role === 'user' ? 'flex-row-reverse' : 'flex-row')}>
              <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0', msg.role === 'user' ? 'bg-slate-700' : 'bg-gradient-to-br from-teal-500 to-cyan-500')}>
                {msg.role === 'user' ? <span className="text-[10px] font-bold text-white">You</span> : <Sparkles className="w-3.5 h-3.5 text-white" />}
              </div>
              <div className={cn('max-w-[85%] rounded-xl px-3 py-2 text-sm', msg.role === 'user' ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-200 border border-slate-700/50')}>
                <span className="whitespace-pre-wrap">{msg.content}</span>
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex items-center gap-2 text-xs text-slate-500 pl-10">
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span>Thinking...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-slate-700/50 bg-slate-800/30">
        <div className="flex items-end gap-2 rounded-xl border border-slate-700/50 bg-slate-800 p-1.5 focus-within:border-teal-500/50 focus-within:ring-1 focus-within:ring-teal-500/30 transition-all">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e) }
            }}
            placeholder="Ask anything... (Enter to send)"
            rows={1}
            className="flex-1 resize-none bg-transparent border-0 focus:outline-none py-1.5 px-2 text-sm leading-relaxed text-slate-200 placeholder:text-slate-500"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className={cn(
              'h-8 w-8 rounded-lg flex items-center justify-center transition-all',
              input.trim()
                ? 'bg-teal-600 hover:bg-teal-500 text-white shadow-lg shadow-teal-500/20'
                : 'bg-slate-700 text-slate-500'
            )}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-center text-[9px] text-slate-500 mt-1.5">
          Shift+Enter for new line •{' '}
          <a href={aiHubUrl} className="text-teal-400 hover:underline">
            Open full AI Hub →
          </a>
        </p>
      </form>
    </div>
  )
}
